import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '40vh',
  minHeight: '300px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 1s ease-in-out',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  backgroundColor: '#ffffff',
  boxShadow: '0 14px 40px rgba(15, 23, 42, 0.10)',
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 24px 70px rgba(15, 23, 42, 0.18)',
  },
}));

const Leadership = () => {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [openLeader, setOpenLeader] = useState(null);

  const banners = [
    '/servicesbanner1.jpg',
    '/banner1.jpg',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [banners.length]);

  const leaders = [
    {
      name: 'Dr. Somnath Basu',
      role: 'Strategic Regulatory Advisor',
      image: 'SomnathBasu.jpg',
      paragraphs: [
        'Dr. Somnath Basu brings a distinguished career spanning regulatory science, public health, and policy advisory roles. His experience includes collaborations with key Government of India institutions such as the Ministry of Health (CDSCO), Ministry of Revenue, and Ministry of Agriculture.',
        'At Thrana, Dr. Basu provides regulatory and scientific oversight, strengthening the company’s alignment with global pharmacovigilance standards. His expertise enhances our commitment to regulatory integrity, scientific rigor, and responsible health technology innovation.',
      ],
    },
    {
      name: 'Sunil Pandita',
      role: 'Chief Executive Officer (CEO)',
      image: 'SunilPandita.jpg',
      paragraphs: [
        'Sunil Pandita brings over 20 years of experience in clinical research, quality assurance, and regulated life sciences environments. His professional background spans Clinical Operations, Data Management, Pharmacovigilance, BIOS, and vendor governance, providing him with a comprehensive understanding of end-to-end drug safety and compliance ecosystems.',
        'As Chief Executive Officer, Sunil provides strategic leadership to Thrana, ensuring the company’s growth is grounded in regulatory integrity and operational excellence. His deep expertise in global regulatory frameworks and GxP compliance shapes Thrana’s product strategy, governance structure, and long-term vision.',
      ],
    },
    {
      name: 'Vasanthi S. E.',
      role: 'Senior Director – Pharmacovigilance & Medical Safety',
      image: 'VasanthiSE.jpg',
      paragraphs: [
        'With nearly 20 years of experience in medical safety projects, delivery management, client engagement, and people leadership, Vasanthi drives Thrana’s pharmacovigilance strategy and service excellence.',
        'She oversees safety operations, delivery governance, partner engagement, and capability development. Her domain expertise ensures that Thrana’s safety solutions are aligned with global regulatory expectations while remaining operationally efficient and scalable.',
      ],
    },
    {
      name: 'Fajo Joy K',
      role: 'Chief Operating Officer (COO)',
      image: 'FajoJoyK.jpg',
      paragraphs: [
        'With over 25 years of experience across digital strategy and enterprise transformation, Fajo has led initiatives at global organisations including Havas Worldwide and Turbostart.',
        'At Thrana, he drives product vision, platform scalability, and ecosystem partnerships. His focus lies in building high-impact digital platforms for regulated industries, ensuring that Thrana evolves as a comprehensive life sciences technology company.',
      ],
    },
    {
      name: 'Anoop Sudhakar',
      role: 'Chief Financial Officer (CFO)',
      image: 'AnoopSudhakar.jpg',
      paragraphs: [
        'Anoop brings over 20 years of experience in finance, insurance, and business development, including more than 15 years in senior leadership roles.',
        'He leads Thrana’s financial strategy, operational governance, and growth planning. His experience in venture-backed growth environments supports disciplined scaling, sustainable expansion, and long-term value creation.',
      ],
    },
    // {
    //   name: 'Muniyaraj Neelemegam',
    //   role: 'Head of Technology',
    //   image: 'muni.jpg',
    //   paragraphs: [
    //     'Muniyaraj brings 10 years of experience in full-stack development, software architecture, blockchain technologies, AI implementations, and relational database systems (RDBMS).',
    //     'At Thrana, he leads platform architecture, cloud infrastructure design, and engineering strategy. His expertise spans scalable backend systems, secure data architectures, and intelligent automation frameworks. Muniyaraj plays a central role in building Thrana’s next-generation life sciences technology stack—ensuring performance, security, interoperability, and regulatory-grade system reliability.',
    //   ],
    // },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Thrana Leadership Team',
    url: 'https://www.thranatech.com/leadership',
    description:
      'Meet the leadership team driving Thrana’s pharmacovigilance innovation, regulatory excellence, and technology strategy.',
  };

  const handleOpenModal = (leader) => {
    setOpenLeader(leader);
  };

  const handleCloseModal = () => {
    setOpenLeader(null);
  };

  return (
    <>
      <SEO
        title="Leadership Team | Thrana"
        description="Meet Thrana’s leadership team bringing together expertise in pharmacovigilance, regulatory science, technology, and business strategy."
        path="/leadership"
        structuredData={structuredData}
      />
      <Box>
        <HeroSection>
          {banners.map((banner, index) => (
            <BannerImage
              key={index}
              sx={{
                backgroundImage: `url(${banner})`,
                opacity: currentBanner === index ? 1 : 0,
              }}
            />
          ))}
          <Overlay>
            <Container maxWidth="md">
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                Leadership Team
              </Typography>
              <Typography
                variant="h6"
                paragraph
                sx={{
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                  color: 'white',
                }}
              >
                The minds driving Thrana’s pharmacovigilance innovation and regulatory excellence.
              </Typography>
            </Container>
          </Overlay>
        </HeroSection>

        <Box sx={{ py: 8, bgcolor: '#f5f7fb' }}>
          <Container maxWidth="lg">
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" align="center" gutterBottom sx={{ color: '#3c005a' }}>
              THRANA LEADERSHIP TEAM
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{ maxWidth: 800, mx: 'auto', color: 'text.secondary' }}
              >
                At Thrana, leadership brings together deep expertise across pharmacovigilance,
                regulatory science, quality systems, enterprise technology, and business strategy.
              </Typography>
            </Box>

            <Grid container spacing={4} justifyContent="center">
              {leaders.map((leader) => (
                <Grid item xs={12} sm={6} md={4} key={leader.name}>
                  <StyledCard>
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '4 / 5',
                        maxHeight: 360,
                        overflow: 'hidden',
                        bgcolor: '#f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        component="img"
                        src={`/${leader.image}`}
                        alt={leader.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/thrana.svg';
                        }}
                      />
                    </Box>
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        p: 3,
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{ fontWeight: 700, color: '#3c005a', mb: 0.5 }}
                      >
                        {leader.name}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 'bold', color: '#3c005a', mb: 2 }}
                      >
                        {leader.role}
                      </Typography>
                      <Box sx={{ flexGrow: 1, mb: 2 }}>
                        {leader.paragraphs.slice(0, 1).map((p, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            paragraph
                            sx={{ textAlign: 'justify', mb: 0 }}
                          >
                            {p}
                          </Typography>
                        ))}
                      </Box>
                      {leader.paragraphs.length > 1 && (
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: '#3c005a',
                            borderColor: '#3c005a',
                            borderWidth: 2,
                            borderRadius: 1,
                            px: 2.5,
                            py: 1,
                            display: 'block',
                            mx: 'auto',
                            mt: 'auto',
                            width: 'fit-content',
                            letterSpacing: '0.5px',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: '#5c0080',
                              color: '#5c0080',
                              backgroundColor: 'transparent',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 8px rgba(60, 0, 90, 0.2)',
                            },
                          }}
                          onClick={() => handleOpenModal(leader)}
                        >
                          READ MORE
                        </Button>
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Dialog
          open={Boolean(openLeader)}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#FFFFFF',
              borderRadius: 3,
            },
          }}
        >
          {openLeader && (
            <>
              <DialogTitle
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontWeight: 700,
                  color: '#fff',
                  bgcolor: '#3c005a',
                  pb: 1.5,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {openLeader.name}
                </Typography>
                {/* <IconButton
                  aria-label="close"
                  onClick={handleCloseModal}
                  sx={{ color: '#fff' }}
                  size="large"
                >
                  <CloseIcon />
                </IconButton> */}
              </DialogTitle>
              <DialogContent dividers sx={{ pt: 3, pb: 2 }}>
                <Grid container spacing={4} alignItems="flex-start">
                  <Grid item xs={12} md={5}>
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '4 / 5',
                        maxHeight: 320,
                        overflow: 'hidden',
                        borderRadius: 2,
                        boxShadow: '0 8px 24px rgba(15,23,42,0.16)',
                        bgcolor: '#f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: { xs: 3, md: 0 },
                      }}
                    >
                      <Box
                        component="img"
                        src={`/${openLeader.image}`}
                        alt={openLeader.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/thrana.svg';
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 'bold', color: '#3c005a', mb: 2 }}
                    >
                      {openLeader.role}
                    </Typography>
                    {openLeader.paragraphs.map((p, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        paragraph
                        sx={{ textAlign: 'justify', mb: 1.5 }}
                      >
                        {p}
                      </Typography>
                    ))}
                  </Grid>
                </Grid>

              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={handleCloseModal}
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#3c005a',
                    color: '#3c005a',
                    '&:hover': {
                      borderColor: '#5c0080',
                      color: '#5c0080',
                    },
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </>
  );
};

export default Leadership;


